import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Dialog, Button } from '@shared/ui';
import { useConsentStore } from '../consentStore';

/**
 * Granular cookie preferences (opened from the banner's "Manage" and, later, Settings/footer). Two
 * categories: strictly-necessary (locked on — Cognito auth/session) and analytics (Hotjar). Saving
 * records the decision (persist + timestamp) which hides the banner and drives the Hotjar gate.
 */
export function ConsentPreferencesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const consent = useConsentStore((s) => s.consent);
  const decide = useConsentStore((s) => s.decide);
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);

  // Re-sync the toggle to the stored value each time the dialog opens.
  useEffect(() => { if (open) setAnalytics(consent?.analytics ?? false); }, [open, consent]);

  const save = () => { decide(analytics); onClose(); };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Cookie preferences"
      description="Choose how Optimus Journal uses cookies. Strictly-necessary cookies keep you signed in and can't be turned off."
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button className="consent-accept" onClick={save}>Save preferences</Button>
      </>}
    >
      <div className="flex flex-col gap-3">
        <ConsentRow
          title="Strictly necessary"
          desc="Sign-in and session (AWS Cognito). Required for the app to work. Always active."
          checked disabled
        />
        <ConsentRow
          title="Analytics"
          desc="Hotjar: session insights and heatmaps that help us improve the journal."
          checked={analytics}
          onChange={setAnalytics}
        />
      </div>
    </Dialog>
  );
}

interface RowProps {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}

function ConsentRow({ title, desc, checked, disabled, onChange }: RowProps) {
  return (
    <div
      className="flex items-start justify-between gap-4 rounded-lg p-3"
      style={{ background: 'var(--canvas-inset)' }}
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-text">{title}</div>
        <div className="mt-0.5 text-xs leading-4 text-text-muted">{desc}</div>
      </div>
      <Switch checked={checked} disabled={disabled} onChange={onChange} label={title} />
    </div>
  );
}

/** Minimal accessible switch (no shared primitive exists). Token-styled → dark/light. */
function Switch({ checked, disabled, onChange, label }: {
  checked: boolean; disabled?: boolean; onChange?: (n: boolean) => void; label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      }`}
      style={{ background: checked ? 'var(--gain)' : 'var(--canvas-divider-strong)' }}
    >
      <span
        className="inline-flex h-4 w-4 translate-x-0.5 items-center justify-center rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
      >
        {checked && <Check className="h-3 w-3" style={{ color: 'var(--gain)' }} />}
      </span>
    </button>
  );
}
